/**
 * Minimal control-plane HTTP client. The browser only talks to the VMS API
 * origin; it never contacts media servers directly.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly headers: Headers;

  constructor(status: number, body: unknown, headers: Headers = new Headers()) {
    super(`API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.headers = headers;
  }

  /** The `error.code` of the API error envelope, if present. */
  get code(): string | undefined {
    const b = this.body as { error?: { code?: unknown } } | null;
    return typeof b?.error?.code === 'string' ? b.error.code : undefined;
  }

  /** Per-field error codes of a `validation_failed` error (field → code). */
  get fields(): Record<string, string> {
    const b = this.body as { error?: { fields?: unknown } } | null;
    const f = b?.error?.fields;
    return f && typeof f === 'object' ? (f as Record<string, string>) : {};
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Não foi possível contatar o servidor', { cause });
    this.name = 'NetworkError';
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Bearer access token. */
  token?: string;
}

const DEFAULT_TIMEOUT_MS = 8000;

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function request<T>(
  method: Method,
  path: string,
  body: unknown,
  opts: RequestOptions,
): Promise<T> {
  const timeout = AbortSignal.timeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout;

  const headers: Record<string, string> = { Accept: 'application/json' };
  // State-changing requests always send JSON: the API relies on it as a CSRF barrier.
  const hasBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
  if (hasBody) headers['Content-Type'] = 'application/json';
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      signal,
      ...(hasBody ? { body: JSON.stringify(body ?? {}) } : {}),
    });
  } catch (err) {
    // Caller-initiated aborts propagate unchanged so callers can ignore them.
    if (opts.signal?.aborted) throw err;
    throw new NetworkError(err);
  }

  const parsed: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, parsed, res.headers);
  return parsed as T;
}

export function getJSON<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, opts);
}

export function postJSON<T>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return request<T>('POST', path, body, opts);
}
